// Google Fit Integration Service (Google Identity Services + REST API)

const GOOGLE_FIT_SCOPES = [
  'https://www.googleapis.com/auth/fitness.activity.read',
  'https://www.googleapis.com/auth/fitness.heart_rate.read'
].join(' ');

export class GoogleFitService {
  static isConnected = false;
  static accessToken = null;
  static tokenClient = null;
  static clientId = null;
  static scriptLoaded = false;
  static scriptPromise = null;

  static async loadScript() {
    if (this.scriptLoaded) return;
    if (this.scriptPromise) return this.scriptPromise;

    this.scriptPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
      if (existing) {
        this.scriptLoaded = true;
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        this.scriptLoaded = true;
        resolve();
      };
      script.onerror = () => reject(new Error('Failed to load Google Identity script'));
      document.head.appendChild(script);
    });

    return this.scriptPromise;
  }

  static async init(clientId) {
    if (!clientId) {
      throw new Error('Missing Google Client ID');
    }
    this.clientId = clientId;
    await this.loadScript();

    if (!window.google?.accounts?.oauth2) {
      throw new Error('Google Identity Services not available');
    }

    if (!this.tokenClient) {
      this.tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: clientId,
        scope: GOOGLE_FIT_SCOPES,
        callback: () => {}
      });
    }

    return true;
  }

  static async connect() {
    if (!this.clientId) {
      throw new Error('Missing Google Client ID');
    }
    await this.init(this.clientId);

    return new Promise((resolve, reject) => {
      this.tokenClient.callback = (response) => {
        if (response?.access_token) {
          this.accessToken = response.access_token;
          this.isConnected = true;
          resolve({ success: true });
          return;
        }
        reject(new Error('Google Fit authorization failed'));
      };

      this.tokenClient.requestAccessToken({ prompt: '' });
    });
  }

  static async fetchAggregate(dataTypeName, startTimeMillis, endTimeMillis) {
    if (!this.accessToken) {
      throw new Error('Google Fit is not connected');
    }

    const body = {
      aggregateBy: [{ dataTypeName }],
      bucketByTime: { durationMillis: 24 * 60 * 60 * 1000 },
      startTimeMillis,
      endTimeMillis
    };

    const response = await fetch('https://www.googleapis.com/fitness/v1/users/me/dataset:aggregate', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${this.accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      throw new Error('Google Fit request failed');
    }

    return response.json();
  }

  static async getStepsToday() {
    const now = Date.now();
    const startOfDay = new Date();
    startOfDay.setHours(0, 0, 0, 0);

    const data = await this.fetchAggregate(
      'com.google.step_count.delta',
      startOfDay.getTime(),
      now
    );

    const steps = (data.bucket || []).reduce((total, bucket) => {
      const points = bucket.dataset?.[0]?.point || [];
      return total + points.reduce((sum, point) => {
        const value = point.value?.[0]?.intVal || 0;
        return sum + value;
      }, 0);
    }, 0);

    return { steps, source: 'google-fit' };
  }

  static async getHeartRateData() {
    const now = Date.now();
    const startOfDay = new Date();
    startOfDay.setHours(0, 0, 0, 0);

    const data = await this.fetchAggregate(
      'com.google.heart_rate.bpm',
      startOfDay.getTime(),
      now
    );

    const readings = [];
    (data.bucket || []).forEach(bucket => {
      const points = bucket.dataset?.[0]?.point || [];
      points.forEach(point => {
        const value = point.value?.[0]?.fpVal;
        if (typeof value === 'number') {
          readings.push(value);
        }
      });
    });

    const heartRate = readings.length
      ? Math.round(readings.reduce((sum, val) => sum + val, 0) / readings.length)
      : 0;

    const minHeartRate = readings.length ? Math.round(Math.min(...readings)) : 0;
    const maxHeartRate = readings.length ? Math.round(Math.max(...readings)) : 0;
    const restingHeartRate = minHeartRate;

    return {
      heartRate,
      minHeartRate,
      maxHeartRate,
      restingHeartRate,
      readingsCount: readings.length,
      source: 'google-fit'
    };
  }

  static disconnect() {
    this.isConnected = false;
    this.accessToken = null;
  }
}

/*
IMPLEMENTATION GUIDE (for later):

1. Set up Google Cloud Project:
   - Go to https://console.cloud.google.com
   - Create new project
   - Enable Fitness API
   - Create OAuth 2.0 credentials (Web application)
   - Add authorized redirect URIs

2. Install dependencies:
   npm install gapi-script

3. Initialize in App.js:
   import { gapi } from 'gapi-script';
   
   useEffect(() => {
     gapi.load('client:auth2', initGoogleFit);
   }, []);

4. Implement OAuth flow:
   - Request scopes: 'https://www.googleapis.com/auth/fitness.activity.read'
   - Get access token
   - Store token securely

5. Make API calls:
   - Use Fitness REST API v1
   - Aggregate data for steps, heart rate, etc.
   - Handle rate limiting and errors

6. Samsung Health sync:
   - User enables Samsung Health  Google Fit sync in Samsung Health app
   - Your app reads from Google Fit
   - Data flows: Samsung Health  Google Fit  Your PWA
*/
